import { TagLabelProps, TagProps } from "@chakra-ui/react";
import { forwardRef } from "react";

import { CompanyIdsFilterItem } from "@/common/components/filters/CompanyIdsFilterItem";
import { FeatureTargetingFilterItem } from "@/common/components/filters/FeatureTargetingFilterItem";
import { UserIdsFilterItem } from "@/common/components/filters/UserIdsFilterItem";
import {
  IcoCompanyAttribute,
  IcoOtherContext,
  IcoUserAttribute,
} from "@/common/components/Icons";
import {
  MotionFilter,
  MotionFilterProps,
} from "@/common/components/MotionComponents";
import { TagRemove } from "@/common/components/TagButtons";
import { UIFilter } from "@reflag/shared/filter";

import { AttributeFilterItem } from "./AttributeFilterItem";
import { CompanyFeatureMetricFilterItem } from "./CompanyFeatureMetricFilterItem";
import { EmptyFilterItem } from "./EmptyFilterItem";
import { SegmentFilterItem } from "./SegmentFilterItem";

type FilterItemTagLabelProps = Omit<TagLabelProps, "filter"> & {
  filter: UIFilter | undefined;
  compact?: boolean;
  showIcon?: boolean;
  noWrap?: boolean;
};

export const FilterItemTagLabel = ({
  filter,
  showIcon = true,
  compact = false,
  noWrap = false,
  ...rest
}: FilterItemTagLabelProps) => {
  return !filter ? (
    <EmptyFilterItem showIcon={showIcon} {...rest} />
  ) : filter.type === "companyAttribute" ? (
    <AttributeFilterItem
      compact={compact}
      filter={filter}
      icon={showIcon ? IcoCompanyAttribute : undefined}
      noWrap={noWrap}
      {...rest}
    />
  ) : filter.type === "companyIds" ? (
    <CompanyIdsFilterItem filter={filter} {...rest} />
  ) : filter.type === "userIds" ? (
    <UserIdsFilterItem filter={filter} {...rest} />
  ) : filter.type === "otherContext" ? (
    <AttributeFilterItem
      compact={compact}
      filter={filter}
      icon={showIcon ? IcoOtherContext : undefined}
      noWrap={noWrap}
      {...rest}
    />
  ) : filter.type === "userAttribute" ? (
    <AttributeFilterItem
      compact={compact}
      filter={filter}
      icon={showIcon ? IcoUserAttribute : undefined}
      noWrap={noWrap}
      {...rest}
    />
  ) : filter.type === "featureMetric" ? (
    <CompanyFeatureMetricFilterItem filter={filter} noWrap={noWrap} {...rest} />
  ) : filter.type === "segment" ? (
    <SegmentFilterItem
      compact={compact}
      filter={filter}
      noWrap={noWrap}
      showIcon={showIcon}
      {...rest}
    />
  ) : filter.type === "featureTargeting" ? (
    <FeatureTargetingFilterItem filter={filter} noWrap={noWrap} {...rest} />
  ) : null;
};

type FilterItemProps = Omit<MotionFilterProps, "filter"> & {
  filter: UIFilter | undefined;
  size?: TagProps["size"];
  showIcon?: boolean;
  showRemove?: boolean;
  compact?: boolean;
  noWrap?: boolean;
  onClick?: () => void;
  onRemove?: () => void;
};

export const FilterItem = forwardRef(
  (
    {
      filter,
      size = "xs",
      showIcon = true,
      showRemove = false,
      compact = false,
      noWrap = false,
      onClick,
      onRemove = () => {},
      ...props
    }: FilterItemProps,
    ref,
  ) => {
    return (
      <MotionFilter
        ref={ref}
        overflow="hidden"
        pos="relative"
        role="group"
        size={compact ? `${size}Compact` : size}
        variant={compact ? "unstyled" : onClick ? "clickable" : "subtle"}
        onClick={onClick}
        {...props}
      >
        <FilterItemTagLabel
          compact={compact}
          filter={filter}
          noWrap={noWrap}
          showIcon={showIcon}
        />
        {showRemove && <TagRemove onRemove={onRemove} />}
      </MotionFilter>
    );
  },
);

FilterItem.displayName = "AttributeFilterItem";
